import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Heart, HeartOff, Search, Trophy } from 'lucide-react';
import {
  fetchMyFavorites,
  removeFavoriteClub,
  friendlyError,
} from '@/lib/api';
import { toast } from 'sonner';
import { ROUTES } from '@/lib/constants';

/**
 * Bloque "Mis clubes favoritos" para la pantalla /cuenta.
 * - Lista clubes seguidos por el usuario.
 * - Permite dejar de seguir desde aquí.
 * - Empty state con CTA al listado de clubes.
 */
export function FavoriteClubsBlock({ onChanged }) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const items = await fetchMyFavorites();
      setFavorites(items);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRemove = async (clubId) => {
    setBusyId(clubId);
    try {
      const { message } = await removeFavoriteClub(clubId);
      toast.success(message || 'Has dejado de seguir el club.');
      setFavorites((curr) => curr.filter((f) => f.club_id !== clubId));
      if (typeof onChanged === 'function') await onChanged();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card
      data-testid="account-favorites-block"
      className="bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)]"
    >
      <div className="p-6 sm:p-7">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-lg text-[var(--ns-text)]">Mis clubes favoritos</h2>
            <p className="text-xs text-[var(--ns-text-muted)] mt-0.5">
              Sigue clubes para recibir sus contenidos y verlos antes que nadie.
            </p>
          </div>
          <Badge
            variant="secondary"
            className="bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] border border-[var(--ns-gold)]/30"
          >
            <Heart className="h-3.5 w-3.5 mr-1 fill-[var(--ns-gold-600)] text-[var(--ns-gold-600)]" />
            {favorites.length}
          </Badge>
        </div>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <div className="flex flex-col items-center text-center py-8 px-4 rounded-xl border border-dashed border-[var(--ns-border)]">
            <div className="h-11 w-11 rounded-2xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center mb-3">
              <Trophy className="h-5 w-5" />
            </div>
            <p className="text-sm text-[var(--ns-text)] font-medium">Aún no sigues ningún club</p>
            <p className="mt-1 text-xs text-[var(--ns-text-muted)] max-w-xs">
              Explora los clubes disponibles y empieza a seguir los que más te gusten.
            </p>
            <Button
              asChild
              className="mt-4 h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0 hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white transition-all"
              data-testid="account-favorites-discover-btn"
            >
              <Link to={ROUTES.clubs}>
                <Search className="h-4 w-4 mr-2" /> Descubrir clubes
              </Link>
            </Button>
          </div>
        ) : (
          <ul className="space-y-2">
            {favorites.map((f) => (
              <li
                key={f.id}
                data-testid={`account-favorite-${f.club_id}`}
                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[var(--ns-border)] hover:border-[var(--ns-gold)]/40 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  {f.club_slug ? (
                    <Link
                      to={ROUTES.clubDetail(f.club_slug)}
                      className="text-sm font-semibold text-[var(--ns-text)] hover:text-[var(--ns-gold-700)] truncate block"
                    >
                      {f.club_name || f.club_slug}
                    </Link>
                  ) : (
                    <span className="text-sm font-semibold text-[var(--ns-text)] truncate block">
                      {f.club_name || f.club_id}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busyId === f.club_id}
                  onClick={() => handleRemove(f.club_id)}
                  className="h-9 px-3 rounded-lg text-[var(--ns-red)] hover:bg-[var(--ns-red)]/10"
                  data-testid={`account-favorite-remove-${f.club_id}`}
                >
                  <HeartOff className="h-4 w-4 mr-1" /> Quitar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
